import { useCallback, useMemo } from 'react'
import { ApolloError } from 'apollo-client'
import {
  toFormData,
  toUpdatePayload,
} from 'domains/account-management/components/account-form/utils/AccountForm.utils'
import { type AccountFormProps } from 'domains/account-management/components/account-form/utils/AccountForm.types'
import { type useUpdateEnterpriseAccount } from 'domains/account-management/api/enterprise-account/update/useUpdateEnterpriseAccount.hooks'
import { trackAccountUpdated } from 'domains/account-management/utils/AccountManagement.tracking'
import { type GetEnterpriseAccountQuery } from 'domains/account-management/api'

type SubmitType = ReturnType<typeof useUpdateEnterpriseAccount>['submit']

export const useUpdateAccount = ({
  submit,
  accountData,
}: {
  submit: SubmitType
  accountData: GetEnterpriseAccountQuery
}) => {
  const onFormSubmit: AccountFormProps['onFormSubmit'] = useCallback(
    formData =>
      submit(toUpdatePayload(formData)).then(result => {
        if (!result.errors) {
          trackAccountUpdated({ formData })
          return
        }

        throw new ApolloError({
          errorMessage: 'There was an error updating the account',
          graphQLErrors: result.errors,
        })
      }),
    [submit]
  )

  const initialFormData = useMemo(() => {
    if (!accountData) return null

    return toFormData(accountData.enterpriseAccount)
  }, [accountData])

  return { onFormSubmit, initialFormData }
}
